import React from 'react';
import styled from 'styled-components';

import { useLanguage } from '../../Context/LanguageContext';
import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';

import { ReactComponent as AiIcon } from '../../image/Ai2.svg';
import { ReactComponent as MemberIcon } from '../../image/Member.svg';
import { ReactComponent as ClassIcon } from '../../image/Class2.svg';
import { ReactComponent as ChartIcon } from '../../image/Chart.svg';
import { ReactComponent as LockerIcon } from '../../image/Locker.svg';
import { ReactComponent as BoardIcon } from '../../image/Board.svg';
import { ReactComponent as NoteIcon } from '../../image/Note2.svg';
import { ReactComponent as NoticeIcon } from '../../image/Notice.svg';
import { ReactComponent as SettingIcon } from '../../image/Setting.svg';
import { ReactComponent as EditIconSvg } from '../../image/Edit.svg';
import { ReactComponent as DeleteIconSvg } from '../../image/Delete.svg';
import { ReactComponent as LinkoLogoSvg } from '../../image/DefaultLogo.svg';

const PERMISSION_KEYS = ['ai', 'member', 'class', 'stats', 'locker', 'board', 'note', 'notice', 'setting'];

const PERMISSION_ICONS = {
  ai: AiIcon,
  member: MemberIcon,
  class: ClassIcon,
  stats: ChartIcon,
  locker: LockerIcon,
  board: BoardIcon,
  note: NoteIcon,
  notice: NoticeIcon,
  setting: SettingIcon
};

export default function SubAccountRow(props) {
  const { data, onClickEdit, onClickDelete } = props;

  const { t } = useLanguage();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const permissionSet = new Set((data.permissions || []).map(key => key.toLowerCase()));

  // 권한 뱃지 1개 //
  function renderPermissionBadge(key) {
    if (!permissionSet.has(key)) return null;

    const IconComponent = PERMISSION_ICONS[key];

    return (
      <PermissionBadge
        key={'SubAccountPermission-' + data.id + '-' + key}
        backgroundColor={theme.buttonFill}>
        <BadgeIconWrapper>
          <StyledPermissionIcon as={IconComponent} color={theme.text} detailColor={theme.buttonFill} />
        </BadgeIconWrapper>
        <BadgeText color={theme.text}>{t('myStoreSetting.account.sub.permission.' + key)}</BadgeText>
      </PermissionBadge>
    );
  }

  return (
    <Row backgroundColor={theme.subBackground}>
      <ProfileColumn>
        <NameRow>
          <NameText color={theme.text}>{data.name}</NameText>
          <NameDot backgroundColor={theme.primary} />
          <RoleText color={theme.text}>{t('myStoreSetting.account.role.subManager')}</RoleText>
        </NameRow>
        <IdRow>
          <LogoBox backgroundColor={theme.buttonFill}>
            <StyledLogo as={LinkoLogoSvg} />
          </LogoBox>
          <IdText color={theme.text}>{data.loginId}</IdText>
        </IdRow>
      </ProfileColumn>
      <VerticalDivider backgroundColor={theme.line} />
      <PermissionList>
        {PERMISSION_KEYS.map(key => renderPermissionBadge(key))}
      </PermissionList>
      <ActionGroup>
        <ActionIconWrapper hoverColor={theme.primary} onClick={onClickEdit}>
          <StyledActionIcon as={EditIconSvg} color={theme.subText} />
        </ActionIconWrapper>
        <ActionIconWrapper data-action='delete' hoverColor={theme.pointRed} onClick={onClickDelete}>
          <StyledActionIcon as={DeleteIconSvg} color={theme.subText} />
        </ActionIconWrapper>
      </ActionGroup>
    </Row>
  );
}

const Row = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 20px;

  width: 100%;
  padding: 10px 20px;
  box-sizing: border-box;

  border-radius: 10px;
  background-color: ${props => props.backgroundColor};

  transition: background-color 0.15s ease-in-out;

  @media (hover: hover) {
    &:has([data-action='delete']:hover) {
      background-color: ${props => props.theme.pointRedAlpha20};
    }
  }
`;

const ProfileColumn = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  gap: 5px;

  width: 180px;

  flex-shrink: 0;
`;

const NameRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 10px;

  width: 100%;
`;

const NameText = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 16px;

  color: ${props => props.color};

  margin: 0 0 0 0;
  white-space: nowrap;
`;

const NameDot = styled.div`
  width: 5px;
  height: 5px;

  border-radius: 1000px;
  background-color: ${props => props.backgroundColor};

  flex-shrink: 0;
`;

const RoleText = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 16px;

  color: ${props => props.color};

  margin: 0 0 0 0;
  white-space: nowrap;
`;

const IdRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 5px;

  width: 100%;
`;

const LogoBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 21px;
  height: 21px;

  border-radius: 2px;
  background-color: ${props => props.backgroundColor};

  flex-shrink: 0;
`;

const StyledLogo = styled.svg`
  width: 18px;
  height: 18px;
`;

const IdText = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.color};

  margin: 0 0 0 0;
`;

const VerticalDivider = styled.div`
  width: 1px;
  height: 30px;

  border-radius: 300px;
  background-color: ${props => props.backgroundColor};

  flex-shrink: 0;
`;

const PermissionList = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 8px;

  flex: 1 0 0;
  min-width: 0;

  flex-wrap: wrap;
`;

const PermissionBadge = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 9.5px;

  height: 28px;
  padding: 0 10px;
  box-sizing: border-box;

  border-radius: 3px;
  background-color: ${props => props.backgroundColor};

  flex-shrink: 0;
`;

const BadgeIconWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 18px;
  height: 18px;
`;

const StyledPermissionIcon = styled.svg`
  width: 18px;
  height: 18px;

  color: ${props => props.color};

  & path:not([stroke]) {
    fill: ${props => props.color};
  }

  & path[fill='#1d1d1d' i] {
    fill: ${props => props.detailColor};
  }
`;

const BadgeText = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.color};

  margin: 0 0 0 0;
  white-space: nowrap;
`;

const ActionGroup = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-end;
  align-items: center;

  gap: 10px;

  width: 67px;

  flex-shrink: 0;
`;

const ActionIconWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 20px;
  height: 20px;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }

    &:hover svg path {
      fill: ${props => props.hoverColor};
    }
  }

  transition: all 0.15s ease-in-out;
`;

const StyledActionIcon = styled.svg`
  width: 18px;
  height: 18px;

  & path {
    fill: ${props => props.color};
  }
`;
